import React, { CSSProperties, useMemo } from "react";
import css from "./index.module.less";
import { currentLocale, getString } from "../index";
import Constant from "../utils/Constant";
import { isRunInWeChat, openUrlInNewTab } from "../utils";
import { usePageContext } from "./index";

const $st = getString;

export interface DownloadAppPopoverItemProps {
  iconSource: string;
  title: string;
  desc: string;
  downloadUrl: any;
  style?: CSSProperties;
}

const DownloadAppPopoverItem = (props: DownloadAppPopoverItemProps) => {
  const { iconSource, title, desc, downloadUrl, style } = props;
  const disableDownload = downloadUrl === "none";
  const _$st = getString;
  const { changeCurrentPage } = usePageContext();
  const sUserAgent = navigator.userAgent.toLowerCase();
  let isMobile =
    /ipad|iphone|midp|rv:1.2.3.4|ucweb|android|windows ce|windows mobile/.test(
      sUserAgent
    );

  const onDownloadButtonClick = () => {
    if (isRunInWeChat()) {
      changeCurrentPage("tutor");
    } else {
      openUrlInNewTab(downloadUrl);
    }
  };

  if (disableDownload) {
    return (
      <div
        className={`${css.download_app_popover_item} ${
          css.download_app_popover_item_disable
        }  ${isMobile ? css.download_app_popover_item_mobile : ""}`}
        style={style}
      >
        <img
          className={`${css.platform_icon} ${
            isMobile ? css.platform_icon_mobile : ""
          }`}
          src={iconSource}
          alt={"download_icon"}
        />
        <div
          className={`${css.download_app_desc_wrap} ${
            isMobile ? css.download_app_desc_wrap_mobile : ""
          }`}
        >
          <span
            className={`${css.download_app_popover_item_title} ${
              isMobile ? css.download_app_popover_item_title_mobile : ""
            }`}
          >
            {title}
          </span>
          <br />
          <span
            className={`${css.download_app_popover_item_desc} ${
              isMobile ? css.download_app_popover_item_desc_mobile : ""
            }`}
          >
            {desc}
          </span>
        </div>
        <div>
          <button
            style={{ fontFamily: "none", lineHeight: "26px" }}
            className={`${css.download_app_button_disable} ${
              isMobile ? css.download_app_button_mobile : ""
            }`}
          >
            {_$st("download_button_text")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${css.download_app_popover_item} ${
        isMobile ? css.download_app_popover_item_mobile : ""
      }`}
      style={style}
    >
      <img
        className={`${css.platform_icon} ${
          isMobile ? css.platform_icon_mobile : ""
        }`}
        src={iconSource}
        alt={"download_icon"}
      />

      <div
        className={`${css.download_app_desc_wrap} ${
          isMobile ? css.download_app_desc_wrap_mobile : ""
        }`}
      >
        <span
          className={`${css.download_app_popover_item_title} ${
            isMobile ? css.download_app_popover_item_title_mobile : ""
          }`}
        >
          {title}
        </span>
        <br />
        <span
          className={`${css.download_app_popover_item_desc} ${
            isMobile ? css.download_app_popover_item_desc_mobile : ""
          }`}
        >
          {desc}
        </span>
      </div>
      <div>
        <button
          style={{ fontFamily: "none", lineHeight: "26px" }}
          onClick={onDownloadButtonClick}
          className={`${css.download_app_button} ${
            isMobile ? css.download_app_button_mobile : ""
          }`}
        >
          {_$st("download_button_text")}
        </button>
      </div>
    </div>
  );
};

export interface DownloadAppPopoverProps {
  isAndroid: boolean;
  style?: React.CSSProperties;
}

export const DownloadAppPopover = (props: DownloadAppPopoverProps) => {
  const { style } = props;
  const sUserAgent = navigator.userAgent.toLowerCase();
  let isMobile =
    /ipad|iphone|midp|rv:1.2.3.4|ucweb|android|windows ce|windows mobile/.test(
      sUserAgent
    );

  const testFlightUrl = useMemo(() => {
    return $st("testflight_url")
  }, []);

  const allDownloadUrl = useMemo(() => {
    const utm_source = new URLSearchParams(window.location.search).get("type"); // 获取utm_source参数
    const gpUrl = utm_source?"https://app.appsflyer.com/com.pionex.client?pid=trial_fund":"https://play.google.com/store/apps/details?id=com.pionex.client&referrer=utm_source%3Dofficialweb";
    return {
      ios: {
        testflight: testFlightUrl,
        appStore: {
          "en-US": "https://apps.apple.com/us/app/pionex/id1485348891",
          "zh-TW": "https://apps.apple.com/tw/app/pionex/id1485348891",
          "zh-CN": "none",
        },
      },
      android: {
        gp: gpUrl,
        apk: `${Constant.pionexApkHost}/apk/pionex.apk`,
      },
    };
  }, [testFlightUrl]);

  const downloadUrl: {
    ios: { testflight: string; appStore: string };
    android: { gp: string; apk: string };
  } = React.useMemo(() => {
    return {
      ios: {
        testflight: allDownloadUrl.ios.testflight,
        appStore:
          allDownloadUrl.ios.appStore[currentLocale] ||
          allDownloadUrl.ios.appStore["en-US"],
      },
      android: {
        gp: allDownloadUrl.android.gp,
        apk: allDownloadUrl.android.apk,
      },
    };
  }, [allDownloadUrl.android.apk, allDownloadUrl.android.gp, allDownloadUrl.ios.appStore, allDownloadUrl.ios.testflight]);

  if (props.isAndroid) {
    return (
      <div
        className={
          isMobile ? css.download_app_popover_mobile : css.download_app_popover
        }
        style={style}
      >
        <DownloadAppPopoverItem
          style={{ marginTop: 10 }}
          iconSource={require("../images/icon_gp.png")}
          title={getString("down_load_gp_version")}
          desc={getString("down_load_gp_version_desc")}
          downloadUrl={downloadUrl.android.gp}
        />
        <DownloadAppPopoverItem
          iconSource={require("../images/icon_apk.png")}
          title={getString("down_load_apk_version")}
          desc={getString("down_load_apk_version_desc")}
          downloadUrl={downloadUrl.android.apk}
        />
      </div>
    );
  } else {
    return (
      <div
        className={
          isMobile ? css.download_app_popover_mobile : css.download_app_popover
        }
        style={style}
      >
        <DownloadAppPopoverItem
          style={{ marginTop: 10 }}
          iconSource={
            downloadUrl.ios.appStore === "none"
              ? require("../images/app_store_disable.png")
              : require("../images/app_store.png")
          }
          title={getString("down_load_app_store_version")}
          desc={getString("down_load_app_store_version_desc")}
          downloadUrl={downloadUrl.ios.appStore}
        />
        <DownloadAppPopoverItem
          iconSource={require("../images/ic_testflight.png")}
          title={getString("down_load_testflight_version")}
          desc={getString("down_load_testflight_version_desc")}
          downloadUrl={downloadUrl.ios.testflight}
        />
      </div>
    );
  }
};
